import { useContext, useMemo } from 'react';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { ContentObjectTypeProvider, AuthInfoProvider, RequestStatuses } from 'content-management-data-lib';
import { ContentUsageLimitsProvider } from 'content-management-restrictions-lib';
import ContentDetailsDataContext from '../context/ContentDetailsDataContext';
import useContentObject from '../hooks/useContentObject';
import ContentSettingsDataProvider from './ContentSettingsDataProvider';
import ReportingFiltersProvider from './ReportingFiltersProvider';
import LanguageVariationsProvider from './LanguageVariationsProvider';
import UsageTrackerPropsProvider from './UsageTrackerPropsProvider';
import { requestStatusLoading } from '../utils/requestStatusUtils';
import ErrorPage from '../components/ErrorPage';
import DetailsRenderConfigProvider from './DetailsRenderConfigProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ContentDetailsDataProvider = ({
  children,
  contentId,
  contentObject: providedContentObject,
  contentObjectType,
  contentGroupObjectType,
  limitNameToLimitTypeMap = {},
  shouldFetchUsageLimits,
  isContentPrerenderingEnabled = false,
  allMetricsData = {},
  defaultMetric = '',
  defaultMetricsToDisplay = [],
  detailsPanelConfigOverrides,
  detailsHeaderConfigOverrides,
  titleConfigOverride,
  trackerProps,
  userInfo,
  contentTypeHasLegacyAnalyticsPages = false
}) => {
  const {
    fetchContentObjectRequestStatus,
    contentObjectRequestErrorData,
    contentObject,
    setContentObject
  } = useContentObject({
    contentObjectType,
    contentId,
    providedContentObject,
    contentTypeHasLegacyAnalyticsPages
  });
  const value = useMemo(() => ({
    contentId,
    contentObject,
    contentObjectRequestErrorData,
    fetchContentObjectRequestStatus,
    isContentPrerenderingEnabled,
    setContentObject
  }), [contentId, contentObject, contentObjectRequestErrorData, fetchContentObjectRequestStatus, isContentPrerenderingEnabled, setContentObject]);
  const usageTrackerProps = useMemo(() => Object.assign({}, trackerProps, {
    objectTypeId: contentObjectType
  }), [trackerProps, contentObjectType]);
  if (requestStatusLoading(fetchContentObjectRequestStatus)) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {
      contextual: true
    });
  }
  if (!contentObject || fetchContentObjectRequestStatus === RequestStatuses.FAILED) {
    return /*#__PURE__*/_jsxs(ContentObjectTypeProvider, {
      contentObjectType: contentObjectType,
      contentGroupObjectType: contentGroupObjectType,
      children: [/*#__PURE__*/_jsx(ErrorPage, Object.assign({}, contentObjectRequestErrorData)), ";"]
    });
  }
  return /*#__PURE__*/_jsx(ContentObjectTypeProvider, {
    contentObjectType: contentObjectType,
    contentGroupObjectType: contentGroupObjectType,
    children: /*#__PURE__*/_jsx(ContentDetailsDataContext.Provider, {
      value: value,
      children: /*#__PURE__*/_jsx(AuthInfoProvider, {
        authInfo: {
          userInfo
        },
        children: /*#__PURE__*/_jsx(UsageTrackerPropsProvider, {
          trackerProps: usageTrackerProps,
          children: /*#__PURE__*/_jsx(ContentUsageLimitsProvider, {
            limitNameToLimitTypeMap: limitNameToLimitTypeMap,
            shouldFetchUsageLimits: shouldFetchUsageLimits,
            contentObjectType: contentObjectType,
            contentGroupObjectType: contentGroupObjectType,
            children: /*#__PURE__*/_jsx(DetailsRenderConfigProvider, {
              detailsPanelConfigOverrides: detailsPanelConfigOverrides,
              detailsHeaderConfigOverrides: detailsHeaderConfigOverrides,
              titleConfigOverride: titleConfigOverride,
              children: /*#__PURE__*/_jsx(ContentSettingsDataProvider, {
                children: /*#__PURE__*/_jsx(LanguageVariationsProvider, {
                  contentObject: value.contentObject,
                  contentId: contentId,
                  children: /*#__PURE__*/_jsx(ReportingFiltersProvider, {
                    allMetricsData: allMetricsData,
                    defaultMetric: defaultMetric,
                    defaultMetricsToDisplay: defaultMetricsToDisplay,
                    children: children
                  })
                })
              })
            })
          })
        })
      })
    })
  });
};
export default ContentDetailsDataProvider;
export function useContentDetailsDataProvider() {
  const context = useContext(ContentDetailsDataContext);
  if (context === undefined) {
    throw new Error('useContentDetailsDataProvider must be used within a ContentDetailsDataProvider');
  }
  return context;
}